import { Box, Grid, Tooltip, Typography } from "@mui/material";
import { Screen } from "../../../main/components/screen/Screen";
import CardBox from "../../../main/components/CardBox";
import { useContext, useEffect, useState } from "react";
import { BasicSS, SismetroListType, SismetroMeta } from "../types";
import { serviceRequestApi } from "../../../services/api";
import Loading from "../../../main/components/Loading";
import EmptyStateNotFound from "../../clients/components/EmptyState";
import { formatDate, formatDateAndHour } from "../../../main/utils/utils";
import { MainContext } from "../../../main/context/MainContextProvider";
import BaseButton from "../../../main/components/BaseButton";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import RopTableSearch from "../../../main/components/RopTableSearch";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import dayjs from "dayjs";
import DateFilterRangeModal from "../components/DateFilterRangeModal";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { RopTablePageSS } from "../../../main/components/RopTable/RopTableSS";

export default function ServiceRequestList() {
  const { isMobile, selectedDashboardUsina: selectedUsina, toastNotifications, setToastNotifications } =
    useContext(MainContext);

  const [serviceRequest, setServiceRequest] = useState<BasicSS[] | null>(null);
  const [lastUpdateAt, setLastUpdateAt] = useState<Date>();
  const [showDateFilter, setShowDateFilter] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Date | dayjs.Dayjs | null>(
    new Date()
  );
  const [endDate, setEndDate] = useState<Date | dayjs.Dayjs | null>(new Date());

  const tooltipMesssage = lastUpdateAt
    ? `Última atualização em ${formatDateAndHour(lastUpdateAt)}`
    : undefined;
  const [selectedStatus, setSelectedStatus] = useState<SismetroListType>(
    SismetroListType.Open
  );
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [serviceRequestMeta, setServiceRequestMeta] =
    useState<SismetroMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const page = pageIndex + 1;
  const navigate = useNavigate();
  async function updateServiceRequest() {
    if (
      !selectedUsina?.usinaIds.idSolicitante &&
      !selectedUsina?.usinaIds.idLocalizacao
    ) {
      const newNotifications = {
        isAdding: true,
        notifications: [
          ...toastNotifications.notifications,
          {
            severity: "warning" as const,
            text: "A usina selecionada não possui um id de solicitante ou localização.",
          },
        ],
      };
      setToastNotifications(newNotifications);
      return;
    }
    setLoading(true);
    try {
      const serviceRequestResponse = await serviceRequestApi.getAllSS(
        selectedUsina?.usinaIds.idSolicitante ?? "",
        page,
        rowsPerPage,
        selectedStatus,
        formatDate(String(startDate), true),
        formatDate(String(endDate), true),
        selectedUsina?.usinaIds.idLocalizacao ?? ""
      );
      setServiceRequest(serviceRequestResponse.data);
      setServiceRequestMeta(serviceRequestResponse.meta);
      if (serviceRequestResponse.meta.total_items < page * 25) {
        setPageIndex(0);
      }
      setLastUpdateAt(new Date());
    } catch (error) {
      setServiceRequest(null);
      setServiceRequestMeta(null);
      throw new Error("Erro ao carregar solicitações de serviço");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    updateServiceRequest();
  }, [
    pageIndex,
    selectedStatus,
    startDate,
    endDate,
    rowsPerPage,
    selectedUsina,
  ]);

  useEffect(() => {
    setPageIndex(0)
    setRowsPerPage(10)
  }, [
    selectedStatus,
    startDate,
    endDate,
    selectedUsina,
  ]);

  return (
    <Screen.Root>
      {loading ? (
        <>
          <Screen.Container>
            <Loading />
          </Screen.Container>
        </>
      ) : (
        <>
          <Screen.Header>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "400",
                paddingTop: "15px",
                paddingBottom: "15px",
              }}
            >
              Solicitações de Serviço
            </Typography>
          </Screen.Header>
          <Screen.Container>
            <Grid
              sx={{
                display: "flex",
                columnGap: 1,
                rowGap: 2,
                width: "100%",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "flex-end",
                alignItems: "center",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              <Grid>
                <BaseButton
                  onClick={() => navigate("/service-request/cadastro")}
                  sx={{ height: "35px" }}
                  title="Criar Solicitação"
                  startIcon={<AddIcon />}
                />
              </Grid>
              <Tooltip title={tooltipMesssage} placement="bottom">
                <Grid>
                  <BaseButton
                    onClick={updateServiceRequest}
                    sx={{ height: "35px" }}
                    title="Atualizar"
                    startIcon={<RestartAltIcon />}
                  />
                </Grid>
              </Tooltip>
              <Grid
                sx={{
                  display: "flex",
                  columnGap: 1,
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <PrimaryBaseButton
                  isActive={selectedStatus === SismetroListType.All}
                  onClick={() => setSelectedStatus(SismetroListType.All)}
                  sx={{ height: "35px" }}
                  title="Todos"
                />
                <PrimaryBaseButton
                  isActive={selectedStatus === SismetroListType.Open}
                  onClick={() => setSelectedStatus(SismetroListType.Open)}
                  sx={{ height: "35px" }}
                  title="Aberta"
                />
                <PrimaryBaseButton
                  isActive={selectedStatus === SismetroListType.Closed}
                  onClick={() => {
                    setSelectedStatus(SismetroListType.Closed);
                    setShowDateFilter(true);
                  }}
                  sx={{ height: "35px" }}
                  title="Fechada"
                />
                <InsertInvitationIcon
                  onClick={() =>
                    selectedStatus === SismetroListType.Closed
                      ? setShowDateFilter(true)
                      : () => { }
                  }
                  sx={{
                    fontSize: "32px",
                    cursor:
                      selectedStatus === SismetroListType.Closed
                        ? "pointer"
                        : "auto",
                    color:
                      selectedStatus === SismetroListType.Closed
                        ? "#FFFFFF"
                        : "#A8B6BC",
                  }}
                />
              </Grid>
              <RopTableSearch search={""} setSearch={() => { }} />
            </Grid>
            {serviceRequest && serviceRequest?.length > 0 ? (
              <>
                <Box
                  sx={{ maxHeight: "500px", overflow: "auto", width: "100%" }}
                >
                  {serviceRequest.map((serviceRequest) => (
                    <CardBox
                      onClick={() =>
                        navigate(`/service-request/${serviceRequest.idSs}`)
                      }
                      sx={{
                        width: "100%",
                        marginBottom: "24px",
                        marginTop: "6px",
                        cursor: "pointer",
                        backgroundColor: "rgb(236,236,236, 0.130)",
                      }}
                      dateLeft={formatDateAndHour(serviceRequest.dataAbertura)}
                    >
                      <Grid
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-around",
                          width: "100%",
                          marginTop: "3px",
                          marginBottom: "3px",
                        }}
                      >
                        <Typography
                          sx={{ fontSize: isMobile ? "12px" : "16px" }}
                          fontWeight="500"
                        >
                          {serviceRequest.solicitante}
                          {serviceRequest.localizacao &&
                            ` - ${serviceRequest.localizacao}`}
                        </Typography>
                        <Typography
                          sx={{ fontSize: isMobile ? "12px" : "16px" }}
                          fontWeight="500"
                        >
                          {serviceRequest.tipoSs}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: "600",
                            fontSize: isMobile ? "12px" : "14px",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {serviceRequest.status}
                        </Typography>
                      </Grid>
                      <hr
                        style={{
                          width: "100%",
                          marginTop: "10px",
                          marginBottom: "10px",
                        }}
                      ></hr>
                      <Typography
                        sx={{
                          marginY: "3px",
                          width: "100%",
                          textAlign: "left",
                          paddingX: "8px",
                          fontSize: isMobile ? "12px" : "16px",
                        }}
                      >
                        {serviceRequest.descricaoSs}
                      </Typography>
                    </CardBox>
                  ))}
                </Box>

                <Grid
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: { md: "flex-end", xs: "center" },
                    flexDirection: "column",
                    paddingRight: { md: "5px", xs: "0px" },
                  }}
                >
                  <RopTablePageSS
                    hasNextPage={serviceRequestMeta?.hasNext ?? false}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    pageIndex={pageIndex}
                    setPage={setPageIndex}
                    setRowsPerPage={setRowsPerPage}
                  />
                </Grid>
              </>
            ) : (
              <Grid
                item
                xs={12}
                sx={{
                  width: "100%",
                  // height: "10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <EmptyStateNotFound title={"Nenhum registro encontrado"} />
              </Grid>
              // <Grid>
              //   <EmptyStateNotFound title={"Nenhum registro encontrado"} />
              // </Grid>
            )}
          </Screen.Container>
        </>
      )}
      <DateFilterRangeModal
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        setOpen={setShowDateFilter}
        open={showDateFilter}
        title="Filtro por data"
      />
    </Screen.Root>
  );
}
